import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, Save, Loader2, Plus, Trash2, Eye, ArrowLeft,
  Target, FileText, BarChart3, Image
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { SidebarProvider } from '../../components/AdminSidebarNew';
import { useAuth } from '../../context/AuthContext';
import { settingsApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const AboutEditorPage = () => {
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  const [content, setContent] = useState({
    title: '',
    subtitle: '',
    description: '',
    history: '',
    mission: '',
    vision: '',
    team: [],
    stats: []
  });

  const canEdit = hasPermission('about.edit');

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const res = await settingsApi.getAbout();
      setContent(res.data);
    } catch (error) {
      console.error('Error fetching about content:', error);
      toast.error('Error al cargar contenido');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!canEdit) {
      toast.error('No tenés permisos para editar');
      return;
    }

    setSaving(true);
    try {
      await settingsApi.updateAbout(content);
      toast.success('Contenido actualizado correctamente');
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  // Stats management
  const addStat = () => {
    setContent(prev => ({
      ...prev,
      stats: [...(prev.stats || []), { label: 'Nueva Estadística', value: '0' }]
    }));
  };

  const updateStat = (index, field, value) => {
    const newStats = [...(content.stats || [])];
    newStats[index] = { ...newStats[index], [field]: value };
    setContent(prev => ({ ...prev, stats: newStats }));
  };

  const removeStat = (index) => {
    setContent(prev => ({
      ...prev,
      stats: prev.stats.filter((_, i) => i !== index)
    }));
  };

  // Team management
  const addTeamMember = () => {
    setContent(prev => ({
      ...prev,
      team: [...(prev.team || []), { name: 'Nuevo Miembro', role: 'Cargo', bio: '', image: '' }]
    }));
  };

  const updateTeamMember = (index, field, value) => {
    const newTeam = [...(content.team || [])];
    newTeam[index] = { ...newTeam[index], [field]: value };
    setContent(prev => ({ ...prev, team: newTeam }));
  };

  const removeTeamMember = (index) => {
    setContent(prev => ({
      ...prev,
      team: prev.team.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <SidebarProvider>
        <AdminLayout>
          <div className="flex items-center justify-center h-64">
            <Loader2 className="animate-spin text-[#D4AF37]" size={32} />
          </div>
        </AdminLayout>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <AdminLayout>
        <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto" data-testid="about-editor-page">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <Link to="/admin/formularios" className="text-[#A1A1AA] hover:text-white text-sm flex items-center gap-1 mb-2">
                <ArrowLeft size={14} />
                Volver a Formularios
              </Link>
              <h1 className="text-2xl font-black uppercase tracking-tight flex items-center gap-3">
                <Users className="text-[#D4AF37]" />
                Quiénes Somos
              </h1>
              <p className="text-[#A1A1AA] text-sm mt-1">
                Edita el contenido de la página "Quiénes Somos"
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setPreviewOpen(true)}
                className="border-[#27272A]"
              >
                <Eye size={16} className="mr-2" />
                Vista Previa
              </Button>
              <Button
                onClick={handleSave}
                disabled={!canEdit || saving}
                className="bg-[#D4AF37] hover:bg-[#B8963C] text-black"
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : <Save size={16} className="mr-2" />}
                Guardar
              </Button>
            </div>
          </div>

          {!canEdit && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 text-yellow-500 px-4 py-3 mb-6 text-sm">
              No tenés permisos para editar esta página. Contacta al administrador.
            </div>
          )}

          <div className="space-y-8">
            {/* Basic Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#121212] border border-[#27272A] p-6"
            >
              <h2 className="text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
                <FileText size={18} className="text-[#D4AF37]" />
                Información Principal
              </h2>
              <div className="space-y-4">
                <div>
                  <Label>Título</Label>
                  <Input
                    value={content.title || ''}
                    onChange={(e) => updateField('title', e.target.value)}
                    placeholder="Quiénes Somos"
                    className="bg-[#0A0A0A] border-[#27272A]"
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <Label>Subtítulo</Label>
                  <Input
                    value={content.subtitle || ''}
                    onChange={(e) => updateField('subtitle', e.target.value)}
                    placeholder="La productora de eventos más importante..."
                    className="bg-[#0A0A0A] border-[#27272A]"
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <Label>Descripción</Label>
                  <textarea
                    value={content.description || ''}
                    onChange={(e) => updateField('description', e.target.value)}
                    placeholder="Descripción principal de la empresa..."
                    className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2 min-h-[100px] text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]"
                    disabled={!canEdit}
                  />
                </div>
              </div>
            </motion.div>

            {/* History */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-[#121212] border border-[#27272A] p-6"
            >
              <h2 className="text-lg font-bold uppercase tracking-tight mb-4">
                Nuestra Historia
              </h2>
              <textarea
                value={content.history || ''}
                onChange={(e) => updateField('history', e.target.value)}
                placeholder="Cuenta la historia de la empresa..."
                className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2 min-h-[150px] text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]"
                disabled={!canEdit}
              />
            </motion.div>

            {/* Mission & Vision */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-[#121212] border border-[#27272A] p-6"
            >
              <h2 className="text-lg font-bold uppercase tracking-tight mb-4 flex items-center gap-2">
                <Target size={18} className="text-[#D4AF37]" />
                Misión y Visión
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Misión</Label>
                  <textarea
                    value={content.mission || ''}
                    onChange={(e) => updateField('mission', e.target.value)}
                    placeholder="Nuestra misión es..."
                    className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2 min-h-[100px] text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]"
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <Label>Visión</Label>
                  <textarea
                    value={content.vision || ''}
                    onChange={(e) => updateField('vision', e.target.value)}
                    placeholder="Nuestra visión es..."
                    className="w-full bg-[#0A0A0A] border border-[#27272A] rounded-md px-3 py-2 min-h-[100px] text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]"
                    disabled={!canEdit}
                  />
                </div>
              </div>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-[#121212] border border-[#27272A] p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold uppercase tracking-tight flex items-center gap-2">
                  <BarChart3 size={18} className="text-[#D4AF37]" />
                  Estadísticas
                </h2>
                {canEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addStat}
                    className="border-[#27272A]"
                  >
                    <Plus size={14} className="mr-1" />
                    Agregar
                  </Button>
                )}
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(content.stats || []).map((stat, index) => (
                  <div key={index} className="bg-[#0A0A0A] border border-[#27272A] p-4 relative group">
                    {canEdit && (
                      <button
                        onClick={() => removeStat(index)}
                        className="absolute top-2 right-2 text-[#52525B] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                    <Input
                      value={stat.value}
                      onChange={(e) => updateStat(index, 'value', e.target.value)}
                      placeholder="100+"
                      className="bg-transparent border-none text-2xl font-bold text-[#D4AF37] text-center p-0 h-auto mb-2"
                      disabled={!canEdit}
                    />
                    <Input
                      value={stat.label}
                      onChange={(e) => updateStat(index, 'label', e.target.value)}
                      placeholder="Eventos"
                      className="bg-transparent border-none text-sm text-[#A1A1AA] text-center p-0 h-auto uppercase"
                      disabled={!canEdit}
                    />
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Team */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-[#121212] border border-[#27272A] p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold uppercase tracking-tight flex items-center gap-2">
                  <Users size={18} className="text-[#D4AF37]" />
                  Equipo
                </h2>
                {canEdit && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addTeamMember}
                    className="border-[#27272A]"
                  >
                    <Plus size={14} className="mr-1" />
                    Agregar Miembro
                  </Button>
                )}
              </div>
              {(content.team || []).length === 0 ? (
                <p className="text-[#52525B] text-sm text-center py-8">
                  No hay miembros del equipo. Haz clic en "Agregar Miembro" para comenzar.
                </p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {(content.team || []).map((member, index) => (
                    <div key={index} className="bg-[#0A0A0A] border border-[#27272A] p-4 relative group">
                      {canEdit && (
                        <button
                          onClick={() => removeTeamMember(index)}
                          className="absolute top-2 right-2 text-[#52525B] hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-[#27272A] rounded-full flex items-center justify-center overflow-hidden">
                            {member.image ? (
                              <img src={member.image} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <Image size={20} className="text-[#52525B]" />
                            )}
                          </div>
                          <div className="flex-1">
                            <Input
                              value={member.name}
                              onChange={(e) => updateTeamMember(index, 'name', e.target.value)}
                              placeholder="Nombre"
                              className="bg-transparent border-none font-bold p-0 h-auto"
                              disabled={!canEdit}
                            />
                            <Input
                              value={member.role}
                              onChange={(e) => updateTeamMember(index, 'role', e.target.value)}
                              placeholder="Cargo"
                              className="bg-transparent border-none text-sm text-[#D4AF37] p-0 h-auto"
                              disabled={!canEdit}
                            />
                          </div>
                        </div>
                        <Input
                          value={member.image || ''}
                          onChange={(e) => updateTeamMember(index, 'image', e.target.value)}
                          placeholder="URL de imagen"
                          className="bg-[#121212] border-[#27272A] text-xs"
                          disabled={!canEdit}
                        />
                        <textarea
                          value={member.bio || ''}
                          onChange={(e) => updateTeamMember(index, 'bio', e.target.value)}
                          placeholder="Breve biografía..."
                          className="w-full bg-[#121212] border border-[#27272A] rounded-md px-3 py-2 min-h-[60px] text-sm text-white placeholder:text-[#52525B] focus:outline-none focus:border-[#D4AF37]"
                          disabled={!canEdit}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>

          {/* Preview Dialog */}
          <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-[#0A0A0A] border-[#27272A]">
              <DialogHeader>
                <DialogTitle className="text-xl font-black uppercase">Vista Previa</DialogTitle>
              </DialogHeader>
              <div className="mt-4 space-y-6">
                {/* Preview Header */}
                <div className="text-center border-b border-[#27272A] pb-6">
                  <h1 className="text-3xl font-black uppercase mb-2">{content.title || 'Quiénes Somos'}</h1>
                  <p className="text-[#D4AF37]">{content.subtitle}</p>
                </div>
                
                {/* Preview Description */}
                <p className="text-[#A1A1AA] text-center">{content.description}</p>
                
                {/* Preview Stats */}
                {content.stats?.length > 0 && (
                  <div className="grid grid-cols-3 gap-4 py-4 border-y border-[#27272A]">
                    {content.stats.map((stat, i) => (
                      <div key={i} className="text-center">
                        <div className="text-2xl font-bold text-[#D4AF37]">{stat.value}</div>
                        <div className="text-xs text-[#A1A1AA] uppercase">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Preview Mission/Vision */}
                <div className="grid md:grid-cols-2 gap-4">
                  {content.mission && (
                    <div className="bg-[#121212] p-4 border border-[#27272A]">
                      <h3 className="font-bold uppercase mb-2">Misión</h3>
                      <p className="text-sm text-[#A1A1AA]">{content.mission}</p>
                    </div>
                  )}
                  {content.vision && (
                    <div className="bg-[#121212] p-4 border border-[#27272A]">
                      <h3 className="font-bold uppercase mb-2">Visión</h3>
                      <p className="text-sm text-[#A1A1AA]">{content.vision}</p>
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </AdminLayout>
    </SidebarProvider>
  );
};

export default AboutEditorPage;
